import React, { useEffect, useMemo, useState } from 'react';
import { Alert, App, Modal, Select } from 'antd';

import { api } from '../../api/client';
import { API_COLLECTIONS_URL } from '../../utils';
import RecordBadge from '../common/RecordBadge';
import { moveItems } from '../../services/subCollections';
import { apiErrorMessage } from '../../utils/apiErrors';
import { parentOf, subCollectionsOf } from '../../utils/collectionFamily';
import { formatCount } from '../../utils/counts';
import { normalizeId } from '../../utils/ids';
import { canCreateItems } from '../../utils/permissions';
import { itemTagCarriedWords } from '../../utils/itemTagSettings';
import { moveWouldExposeWords } from '../../utils/elementGrantRefusals';

/** Why one record could not move (keepr-api POST /api/items/move `failures[].code`). */
export const MOVE_FAILURE_WORDS = {
	card_not_effective: 'its card cannot be used there',
	not_in_family: 'it is not in this family of collections',
	item_locked: 'it is locked — a manager can unlock it first',
	lookup_not_found: 'it points at a record you could not choose there',
	forbidden: 'you cannot add items of its card there',
	// Tags (T2b, T2c): a restricted tag the new collection cannot hold, and
	// an item used as a tag whose tag the move would take off other items
	// (B20) — each a manager's act.
	restricted_tag: 'it carries a restricted tag the collection it would go to cannot hold, and only a manager of the tag’s collection can take it off',
	item_tag_carried: 'it is used as a tag on other items outside where it would go. Only a manager of its collection can move it',
	account_link_needs_access: 'it is linked to a person, and there that adds them to a group with more access than you have — ask a manager to move it',
	// KPR-801: only a manager of a record's collection deletes its note from
	// before comments, and the move would hand that to someone who is not.
	migrated_note_carried: 'it has a note from before comments, which only a manager of its collection can delete, and you manage the collection it would go to — ask a manager to move it',
};

/** One refused record's reason, with its counts where the code has them. */
export function moveFailureWords(failure = {}) {
	if (failure?.code === 'item_tag_carried') return itemTagCarriedWords(failure);
	if (failure?.code === 'move_would_expose') return moveWouldExposeWords(failure);
	return MOVE_FAILURE_WORDS[failure?.code] || 'it cannot move there';
}

/**
 * The collections of `collection`'s family, parent first, each a summary
 * with the caller's `myAccess` where known. At a sub-collection the rest of
 * the family is read off its parent (`parentDoc`), when the caller can open
 * it.
 */
export function familyMembers(collection, parentDoc = null) {
	const root = parentDoc || (subCollectionsOf(collection).length ? collection : null);
	if (!root) return collection ? [collection] : [];
	return [
		{ _id: normalizeId(root._id), name: root.name, icon: root.icon, color: root.color, myAccess: root.myAccess },
		...subCollectionsOf(root),
	];
}

/**
 * BULK MOVE TO… (docs/plans/sub-collections.md §5.5): the selected records
 * go to another collection of the same family — the parent or one of its
 * sub-collections — all or nothing. Offered only where the caller can add
 * items; the API judges each record (its card must be usable there, its
 * lookups must be ones the mover could write there, a locked one waits for
 * a manager) and a refusal lists every record it names, with why.
 */
export default function ItemsMoveModal({ open, onClose, collection, items = [], ids = [], titleOf, onMoved }) {
	const { message } = App.useApp();
	const [parentDoc, setParentDoc] = useState(null);
	const [target, setTarget] = useState(null);
	const [busy, setBusy] = useState(false);
	const [refusal, setRefusal] = useState(null);

	const parent = parentOf(collection);
	useEffect(() => {
		if (!open) return undefined;
		setTarget(null);
		setRefusal(null);
		if (!parent?.readable) { setParentDoc(null); return undefined; }
		let live = true;
		api.get(`${API_COLLECTIONS_URL}/${parent._id}`)
			.then(({ data }) => { if (live) setParentDoc(data || null); })
			.catch(() => { if (live) setParentDoc(null); });
		return () => { live = false; };
	}, [open, parent?._id, parent?.readable]);

	const options = useMemo(() => familyMembers(collection, parentDoc)
		// Offered where the caller can add items; an unknown access (a
		// summary without it) is offered and left to the API.
		.filter(member => !member.myAccess || canCreateItems(member)), [collection, parentDoc]);

	const titles = useMemo(() => new Map((items || []).map(item => [normalizeId(item?._id), typeof titleOf === 'function' ? titleOf(item) : null])), [items, titleOf]);
	const chosen = options.find(member => normalizeId(member._id) === target) || null;

	// Into a collection whose attachments are OFF (PR 10b, the 10b contract):
	// files in file elements are hidden there — never refused, never counted.
	// The family's summaries do not carry the switch, so the chosen
	// collection is read once unless it is one already in hand. Said only
	// when a moving record holds files (its read carries their facts).
	const movesFiles = (items || []).some(item => item?.files && typeof item.files === 'object' && Object.keys(item.files).length > 0);
	const [targetDoc, setTargetDoc] = useState(null);
	useEffect(() => {
		setTargetDoc(null);
		if (!open || !target || !movesFiles) return undefined;
		const known = [collection, parentDoc].find(doc => doc && normalizeId(doc._id) === target && typeof doc.allowAttachments === 'boolean');
		if (known) { setTargetDoc(known); return undefined; }
		let live = true;
		api.get(`${API_COLLECTIONS_URL}/${target}`)
			.then(({ data }) => { if (live) setTargetDoc(data || null); })
			.catch(() => { /* no warning without an answer; the move is never refused for it */ });
		return () => { live = false; };
	}, [open, target, collection, parentDoc, movesFiles]);
	const filesHidden = movesFiles && Boolean(targetDoc) && targetDoc.allowAttachments !== true;

	const submit = async () => {
		if (!target) return;
		setBusy(true);
		setRefusal(null);
		try {
			const result = await moveItems(ids, target);
			const moved = Number(result?.moved) || 0;
			message.success(moved
				? `${formatCount(moved, 'item')} moved to ${chosen?.name || 'the collection'}.`
				: `Those items are already in ${chosen?.name || 'that collection'}.`);
			onMoved?.(result);
			onClose?.();
		} catch (error) {
			const failures = Array.isArray(error?.response?.data?.failures) ? error.response.data.failures : [];
			setRefusal({ message: apiErrorMessage(error, 'The items could not be moved.'), failures });
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal
			open={open}
			title={`Move ${formatCount(ids.length, 'item') || 'items'}`}
			okText="Move"
			okButtonProps={{ disabled: !target, loading: busy }}
			onOk={submit}
			onCancel={onClose}
			destroyOnHidden
			className="items-move-modal"
		>
			<p>
				Each record keeps its values, its number and its attachments, and opens in its new collection.
				Who can see it follows that collection&rsquo;s sharing.
			</p>
			<Select
				style={{ width: '100%' }}
				placeholder="Choose where they go"
				aria-label="Move to"
				value={target || undefined}
				onChange={setTarget}
				options={options.map(member => ({
					value: normalizeId(member._id),
					label: <RecordBadge kind="collection" record={member} title={member.name || 'Collection'} size="xs" />,
				}))}
			/>
			{filesHidden ? (
				<Alert
					className="mt-2"
					type="warning"
					showIcon
					data-testid="move-files-hidden"
					message={`Files in file elements will be hidden there: attachments are off in ${targetDoc?.name || chosen?.name || 'that collection'}.`}
				/>
			) : null}
			{refusal ? (
				<Alert
					className="mt-2"
					type="warning"
					showIcon
					message={refusal.message}
					description={refusal.failures.length ? (
						<ul className="danger-zone__blockers">
							{refusal.failures.map(failure => (
								<li key={failure.itemId}>
									<strong>{titles.get(normalizeId(failure.itemId)) || 'A record'}</strong>: {moveFailureWords(failure)}
								</li>
							))}
						</ul>
					) : null}
				/>
			) : null}
		</Modal>
	);
}
